/**
 * Ruthik Services Marquee
 *
 * Per column: continuous vertical translate. Direction is set per column
 * via data-direction="up|down". Track content is rendered TWICE in the
 * Liquid template, so a single linear translate of -halfHeight (or
 * +halfHeight for down) creates a seamless loop — when GSAP repeats and
 * snaps back, the visual state is identical because the second copy
 * positioned itself exactly where the first copy started.
 *
 * Pause: hovering the section pauses all columns; visibility-hidden tabs
 * pause too. prefers-reduced-motion stops the animation entirely.
 */
(function () {
  'use strict';

  var initialized = new WeakSet();

  function whenGsapReady(cb) {
    if (window.gsap) return cb(window.gsap);
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      if (window.gsap) {
        clearInterval(timer);
        cb(window.gsap);
      } else if (tries > 80) {
        clearInterval(timer);
      }
    }, 100);
  }

  function setupSection(root, gsap) {
    if (initialized.has(root)) return;
    initialized.add(root);

    var enabled = root.getAttribute('data-motion') !== 'false';
    var speed = parseFloat(root.getAttribute('data-speed')) || 50;
    var columns = Array.from(root.querySelectorAll('[data-marquee-col]'));
    if (!columns.length) return;

    var mm = gsap.matchMedia();
    var tweens = [];

    mm.add(
      { motionOk: '(prefers-reduced-motion: no-preference)' },
      function (ctx) {
        if (!enabled || !ctx.conditions.motionOk) return;

        /* Wait for ALL images inside the marquee to load before measuring
           track heights. Without this, scrollHeight reports 0 (or the
           placeholder height) and the marquee silently fails to animate. */
        var allImages = Array.from(root.querySelectorAll('[data-marquee-track] img'));
        var imagesReady;
        if (allImages.length === 0) {
          imagesReady = Promise.resolve();
        } else {
          imagesReady = Promise.all(allImages.map(function (img) {
            if (img.complete && img.naturalHeight > 0) return Promise.resolve();
            return new Promise(function (resolve) {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
              /* Hard cap so a failed image doesn't block the others forever. */
              setTimeout(resolve, 4000);
            });
          }));
        }

        imagesReady.then(function () {
          /* Each column owns its own track + tween. Tracks are rendered
             twice from Liquid (two consecutive copies of the image list),
             so halfHeight equals one full copy. */
          columns.forEach(function (col) {
            var track = col.querySelector('[data-marquee-track]');
            if (!track) return;
            var direction = (col.getAttribute('data-direction') || 'up').toLowerCase();
            var halfHeight = track.scrollHeight / 2;
            if (halfHeight < 80) return; /* nothing to animate */

            /* Position track at its starting offset. For "up", start at 0
               and animate to -halfHeight. For "down", start at -halfHeight
               and animate to 0. Both give a continuous infinite scroll
               because the duplicated content makes the reset visually
               identical to the end state. */
            gsap.set(track, { y: direction === 'down' ? -halfHeight : 0 });
            var tween = gsap.to(track, {
              y: direction === 'down' ? 0 : -halfHeight,
              duration: speed,
              ease: 'none',
              repeat: -1
            });
            tweens.push(tween);
          });
        });

        /* Pause on hover — listeners on both the section root AND the
           marquee stage element so pause fires reliably whether the user
           crosses into the section's bounds or directly into a column.
           Using pointerover/pointerout (not enter/leave) so it works even
           when hovering between adjacent columns inside the stage. */
        var marqueeStage = root.querySelector('[data-marquee-stage]') || root;
        function pauseAll() { tweens.forEach(function (t) { t.pause(); }); }
        function playAll()  { tweens.forEach(function (t) { t.play(); }); }
        /* mouseenter/leave on the stage handles the common case. */
        marqueeStage.addEventListener('mouseenter', pauseAll);
        marqueeStage.addEventListener('mouseleave', playAll);
        /* Touch devices: pause on touchstart, resume on touchend. */
        marqueeStage.addEventListener('touchstart', pauseAll, { passive: true });
        marqueeStage.addEventListener('touchend', playAll, { passive: true });

        /* Pause when tab hidden. */
        function onVis() {
          if (document.hidden) tweens.forEach(function (t) { t.pause(); });
          else tweens.forEach(function (t) { t.play(); });
        }
        document.addEventListener('visibilitychange', onVis);

        /* Recalculate on resize — track halfHeight changes when image
           widths reflow. Debounced. */
        var resizeTimer;
        function onResize() {
          window.clearTimeout(resizeTimer);
          resizeTimer = window.setTimeout(function () {
            tweens.forEach(function (t) { t.kill(); });
            tweens.length = 0;
            columns.forEach(function (col) {
              var track = col.querySelector('[data-marquee-track]');
              if (!track) return;
              var direction = (col.getAttribute('data-direction') || 'up').toLowerCase();
              var halfHeight = track.scrollHeight / 2;
              if (halfHeight < 100) return;
              gsap.set(track, { y: direction === 'down' ? -halfHeight : 0 });
              tweens.push(gsap.to(track, {
                y: direction === 'down' ? 0 : -halfHeight,
                duration: speed,
                ease: 'none',
                repeat: -1
              }));
            });
          }, 200);
        }
        window.addEventListener('resize', onResize);

        return function cleanup() {
          tweens.forEach(function (t) { t.kill(); });
          tweens.length = 0;
          document.removeEventListener('visibilitychange', onVis);
          window.removeEventListener('resize', onResize);
        };
      }
    );
  }

  function initAll(gsap) {
    document.querySelectorAll('[data-marquee-root]').forEach(function (root) {
      setupSection(root, gsap);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { whenGsapReady(initAll); });
  } else {
    whenGsapReady(initAll);
  }

  /* Shopify editor: re-init when the section is reloaded. */
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-marquee-root]');
    if (root) whenGsapReady(function (gsap) { setupSection(root, gsap); });
  });
})();
